import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import InfoIcon from '@mui/icons-material/Info';
import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Theme,
  Tooltip,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { styled } from '@mui/system';
import { GridSortModel } from '@mui/x-data-grid';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import {
  IBusinessModel,
  useGetBusinessCampaignsQuery,
  useGetBusinessProfileBrandsQuery,
  useSyncBusinessCampaignDataMutation,
  useSyncProfileBrandDataMutation,
  useUpdateCampaignMutation,
} from '../../api/apigQuery';
import {
  trackAdd10DLCCampaign,
  trackReSync10DLC,
} from '../../common/userTracking';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import RegisterBrand from '../components/modals/RegisterBrand';
import RegisterCampaignModal from '../components/modals/RegisterCampaignModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { uuidPattern } from '../constants/regex';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

const useStyles = makeStyles((theme: Theme) => ({
  brandContainer: {
    backgroundColor: theme.palette.mode === 'dark' ? '#3a3a3a' : '#ffffff',
  },
  brandTitleRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '24px 24px 48px 24px',
  },
  titleText: {
    fontSize: '18px',
  },
  rightComponentContainer: {
    maxWidth: 'none !important',
    width: '100% !important',
    minWidth: '400px !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignContent: 'flex-end',
  },
  numbersList: {
    padding: '0',
    paddingTop: '8px',
    paddingBottom: '1px',
    width: '100%',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  grayIcon: {
    color: 'gray',
  },
}));
export interface IKixieCampaignData {
  plivoCampaignId: string;
  plivoBrandId: string;
  businessId: number;
  kixieFormJson: string;
  plivoReturnJson: string;
  isProcessed: number;
  campaignStatus: string;
  isGeneric: number;
  useCse: string;
  subuseCse: string;
  mainTemplate: number;
  kixieCampaignId: string;
  campaignId?: string;
}

export interface IParsedCampaign {
  brandId: string;
  campaignAlias: string;
  campaignAttributes: {
    ageGated: boolean;
    embeddedLink: boolean;
    directLending: boolean;
    embeddedPhone: boolean;
    subscriberHelp: boolean;
    affiliateMarketing?: boolean;
    numberPooling?: boolean;
    [key: string]: any; // to allow additional dynamic attributes if needed
  };
  campaignId: string;
  campaignSource: string;
  createdAt: string; // ISO date string
  description: string;
  helpMessage: string;
  mnoMetadata: {
    atT?: object;
    tMobile?: object;
    usCellular?: object;
    verizonWireless?: object;
    [key: string]: any;
  };
  optinMessage: string;
  optoutMessage: string;
  registrationStatus: string;
  resellerId: string;
  sample1: string;
  sample2: string;
  subUsecase: string; // comma-separated list
  subusecase: string; // duplicate field, possibly a typo or legacy
  usecase: string;
  vertical: string;
  kixieFormJson?: string;
  plivoReturnJson?: string;
  kixieCampaignId?: string;
}
// Safe JSON parse helper that returns default value on error
const safeJsonParse = <T,>(
  jsonString: string | undefined | null,
  defaultValue: T
): T => {
  if (!jsonString) return defaultValue;
  try {
    return JSON.parse(jsonString) as T;
  } catch {
    return defaultValue;
  }
};

export default function Manage10DLC() {
  const userAtomLoadable = useRecoilValueLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;

  const [selectedBrandIndex, setSelectedBrandIndex] = useState(0);

  const {
    data: businessProfileBrands,
    isSuccess: profileBrandsSuccess,
    isLoading: profileBrandsLoading,
    refetch: refetchProfileBrands,
  } = useGetBusinessProfileBrandsQuery(
    {
      userSettings: {
        businessId: (userAtomContents as IUserAtom).businessid,
        apikey: (userAtomContents as IUserAtom).apikey,
        userid: (userAtomContents as IUserAtom).userid,
      },
    },
    {
      skip:
        userAtomLoadable.state !== 'hasValue' ||
        !(userAtomContents as IUserAtom)?.businessid ||
        !(userAtomContents as IUserAtom)?.apikey ||
        !(userAtomContents as IUserAtom)?.userid,
    }
  );

  const [
    ,
    brandStatusLoadable,
    setRefreshToggleForBrandStatus,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_10DLC_STATUS_MESSAGE,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_10DLC_STATUS_MESSAGE,
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );

  const profileBrands = useMemo(() => {
    if (!profileBrandsLoading && profileBrandsSuccess) {
      const mappedBrands =
        businessProfileBrands?.result?.map((brand) => ({
          ...brand,
          kixieBrandFormJson: safeJsonParse(brand?.kixieBrandFormJson, {}),
          kixieProfileFormJson: safeJsonParse(brand?.kixieProfileFormJson, {}),
          plivoBrandJson: safeJsonParse(brand?.plivoBrandJson, {}),
          plivoProfileJson: safeJsonParse(brand?.plivoProfileJson, {}),
        })) ?? [];

      return mappedBrands.length > 0
        ? mappedBrands
        : [
            {
              brandRegistrationStatus: '',
              kixieBrandFormJson: {},
              kixieProfileFormJson: {},
              plivoBrandJson: {},
              plivoProfileJson: {},
            },
          ];
    }
    return [
      {
        brandRegistrationStatus: '',
        kixieBrandFormJson: {},
        kixieProfileFormJson: {},
        plivoBrandJson: {},
        plivoProfileJson: {},
      },
    ];
  }, [businessProfileBrands, profileBrandsSuccess, profileBrandsLoading]);

  useEffect(() => {
    setRefreshToggleForBrandStatus({});
  }, []);

  // Ensure selectedBrandIndex is within bounds
  const safeSelectedBrandIndex = useMemo(() => {
    if (profileBrands.length === 0) return 0;
    return Math.min(selectedBrandIndex, profileBrands.length - 1);
  }, [selectedBrandIndex, profileBrands.length]);

  useEffect(() => {
    if (
      profileBrands.length > 0 &&
      selectedBrandIndex >= profileBrands.length
    ) {
      setSelectedBrandIndex(profileBrands.length - 1);
    }
  }, [profileBrands.length, selectedBrandIndex]);

  return (
    <Paper>
      {profileBrands.length > 1 && (
        <Tabs
          value={safeSelectedBrandIndex}
          onChange={(_, newIndex) => setSelectedBrandIndex(newIndex)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {profileBrands.map((brand, index) => {
            const tabLabel = brand.brandId || `Brand ${index + 1}`;
            return (
              <Tab key={brand.brandId || `brand-${index}`} label={tabLabel} />
            );
          })}
        </Tabs>
      )}
      {profileBrands.length > 0 && (
        <BrandPanel
          brandData={profileBrands[safeSelectedBrandIndex]}
          userAtomContents={userAtomContents}
          refetchProfileBrands={refetchProfileBrands}
          brandStatusLoadable={brandStatusLoadable}
          profileBrands={profileBrands}
          profileBrandsSuccess={profileBrandsSuccess}
          profileBrandsLoading={profileBrandsLoading}
          businessProfileBrands={businessProfileBrands}
        />
      )}
    </Paper>
  );
}

function BrandPanel({
  brandData,
  userAtomContents,
  refetchProfileBrands,
  brandStatusLoadable,
  profileBrands,
  profileBrandsSuccess,
  profileBrandsLoading,
  businessProfileBrands,
}) {
  const [sortModel, setGridSortModel] = useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);
  const classes = useStyles();
  const [userAtom, setUserAtom] = useRecoilState(user);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [updateCampaign] = useUpdateCampaignMutation();
  const [parsedSuccessfulCampaigns, setParsedCampaigns] = useState();
  const [selectedCampaignToCopy, setCopyCampaign] = useState();
  const [isLoadingSync, setIsLoadingSync] = useState(false);
  const [showRegisterCampaignModal, setShowRegisterCampaignModal] = useState(
    false
  );
  const [showRegisterBrandModal, setShowRegisterBrandModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [campaignId, setCampaignId] = useState();
  const isSuperAdmin = useMemo(
    () => userAtomContents?.user?.role?.toLowerCase() === 'superadmin',
    [userAtomContents]
  );
  const [syncCampaign] = useSyncBusinessCampaignDataMutation();
  const [syncProfileBrand] = useSyncProfileBrandDataMutation();
  const [campaignSyncLoading, setCampaignSyncLoading] = useState(false);
  const [isNewBrand, setIsNewBrand] = useState(false);

  const isProcessing = useMemo((): boolean => {
    const plivoProfile = brandData?.plivoProfileJson?.profile ?? {};
    const plivoBrand = brandData?.plivoBrandJson?.brand ?? {};

    const profileRegistrationStatus =
      plivoProfile?.profileStatus ??
      brandData?.plivoProfileJson?.registrationStatus ??
      '';

    const brandRegistrationStatus =
      plivoBrand?.registrationStatus ??
      brandData?.plivoBrandJson?.registrationStatus ??
      '';

    const profileMessage =
      brandData?.plivoProfileJson?.message?.toLowerCase?.() ?? '';

    const brandMessage =
      brandData?.plivoBrandJson?.message?.toLowerCase?.() ?? '';

    return (
      (!profileRegistrationStatus &&
        profileMessage.includes('being processed')) ||
      profileRegistrationStatus?.toLowerCase() === 'processing' ||
      (!brandRegistrationStatus && brandMessage.includes('being processed')) ||
      brandRegistrationStatus?.toLowerCase() === 'processing'
    );
  }, [brandData]);

  useEffect(() => {
    const result = brandStatusLoadable?.contents?.response?.data?.result;
    if (brandStatusLoadable?.state === 'hasValue' && Array.isArray(result)) {
      // 3. Compare with existing userAtom values
      const currentBrandStatus =
        userAtomContents?.brandStatus10Dlc?.brandStatus;
      const currentNumberCount =
        userAtomContents?.brandStatus10Dlc?.businessNumbersCount;

      const updatedBrandStatus = {
        brandStatus: result?.[0]?.status ?? '',
        businessNumbersCount: result?.[0]?.count ?? 0,
      };

      if (
        currentBrandStatus !== updatedBrandStatus?.brandStatus ||
        currentNumberCount !== updatedBrandStatus?.businessNumbersCount
      ) {
        const updatedUserAtom = {
          ...userAtom,
          brandStatus10Dlc: updatedBrandStatus,
        };
        setUserAtom(updatedUserAtom);
      }
    }
  }, [
    brandStatusLoadable,
    setUserAtom,
    userAtom,
    userAtomContents?.brandStatus10Dlc?.brandStatus,
    userAtomContents?.brandStatus10Dlc?.businessNumbersCount,
  ]);
  const [businessDataCached] = makeApiRequestCached<IBusinessModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );

  const {
    data: campaigns,
    isSuccess: campaignsSuccess,
    isLoading: campaignsLoading,
    refetch: refetchCampaigns,
  } = useGetBusinessCampaignsQuery(
    {
      userSettings: {
        businessId: (userAtomContents as IUserAtom).businessid,
        apikey: (userAtomContents as IUserAtom).apikey,
        userid: (userAtomContents as IUserAtom).userid,
      },
    },
    {
      skip: !userAtomContents || !('businessid' in userAtomContents),
    }
  );

  const plivoBrandStatus = useMemo(() => {
    return brandData?.plivoBrandJson?.brand?.registrationStatus ?? '';
  }, [brandData]);
  const plivoBrandRegistrationStatus = useMemo(
    () =>
      brandData?.plivoBrandJson?.brand?.brandType?.toLowerCase() === 'failed'
        ? 'FAILED'
        : brandData?.plivoBrandJson?.brand?.registrationStatus ??
          brandData?.brandRegistrationStatus ??
          '',
    [brandData]
  );

  const campaignTemplate = useMemo(() => {
    if (campaignsSuccess && campaigns?.result?.length > 0) {
      return campaigns?.result?.filter(
        (campaign) => campaign?.mainTemplate === 1
      )[0];
    }
  }, [campaignsSuccess, campaigns]);

  type CampaignRow = {
    id: string;
    plivoBrandId: string;
    campaignStatus: string;
    numbers: unknown[];
    plivoReturn: unknown;
  };

  const rowsCached = useRef<CampaignRow[]>([]);

  rowsCached.current = useMemo(
    () =>
      campaignsLoading === false
        ? campaigns?.result
            ?.filter(
              (campaign) =>
                campaign?.mainTemplate !== 1 &&
                campaign?.plivoBrandId === brandData?.brandId
            )
            ?.map((el) => ({
              id: el?.plivoCampaignId ?? 'none',
              plivoBrandId: el?.plivoBrandId ?? '',
              campaignStatus: el?.campaignStatus ?? '',
              numbers: safeJsonParse(el.Numbers, []),
              plivoReturn: safeJsonParse(el?.plivoReturnJson, {}),
            })) ?? []
        : rowsCached?.current ?? [],
    [campaignsLoading, campaigns, brandData]
  );

  const successfulCampaigns = useMemo(() => {
    if (campaignsLoading === false) {
      if (Number(campaigns?.result?.length) > 0) {
        return campaigns?.result?.filter(
          (campaign) =>
            String(campaign?.campaignStatus)?.toLowerCase() === 'active'
        );
      }
    }
  }, [campaignsLoading, campaigns]);

  const handleSyncCampaign = async (campaignId: string) => {
    try {
      setCampaignSyncLoading(true);
      const response = await syncCampaign({
        userSettings: {
          apikey: userAtomContents?.apikey,
          businessId: userAtomContents?.businessid,
          userid: userAtomContents?.userid,
        },
        campaignId: campaignId,
      });
      if (response && (response as any)?.data?.success === true) {
        setCampaignSyncLoading(false);
        enqueueSnackbarWithStyles({
          message: `Campaign ${campaignId} synced successfully`,
          alertProps: { severity: 'success' },
        });
        refetchCampaigns();
      }
    } catch (error) {
      enqueueSnackbarWithStyles({
        message: `Campaign ${campaignId} synced failed`,
        alertProps: { severity: 'warning' },
      });
    }
    setCampaignSyncLoading(false);
  };

  const columns = [
    {
      field: 'id',
      headerName: 'Campaign ID',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'plivoBrandId',
      headerName: 'Brand ID',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'numbers',
      headerName: 'Numbers',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      enableSorting: false,
      renderCell: (params) => {
        return (
          <>
            <ul className={classes.numbersList}>
              {Array.isArray(params.value) && params.value?.length > 4 ? (
                <>
                  {params.value?.slice(0, 4).map((number, i) => (
                    <li key={number + i}>{number}</li>
                  ))}
                  <li>
                    <NavLink to="/manage/numbers/unblocked">
                      + {params.value.length - 4} more ...
                    </NavLink>
                  </li>
                </>
              ) : Array.isArray(params.value) ? (
                params.value.map((number, i) => (
                  <li key={number + i}>{number}</li>
                ))
              ) : (
                <></>
              )}
            </ul>
          </>
        );
      },
    },
    {
      field: 'campaignStatus',
      headerName: 'Campaign Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => {
        const plivoReturn = params?.row?.plivoReturn;

        let status =
          params?.value === '' &&
          plivoReturn?.message?.toLowerCase()?.includes('is being processed')
            ? 'PROCESSING'
            : String(params?.value ?? '')?.toUpperCase();
        if (params?.value === '') {
          if (
            plivoReturn?.message?.toLowerCase()?.includes('is being processed')
          ) {
            status = 'PROCESSING';
          }
        }
        let statusMessage = plivoReturn?.message ?? '';
        if (
          status === 'FAILED' &&
          (plivoReturn?.ErrorCode ||
            plivoReturn?.campaign?.errorCode ||
            plivoReturn?.ErrorMessage ||
            plivoReturn?.campaign?.errorReason ||
            plivoReturn?.ProfileUUID)
        ) {
          const errorCode =
            plivoReturn?.ErrorCode ?? plivoReturn?.campaign?.errorCode;
          const errorMessage =
            plivoReturn?.ErrorMessage || plivoReturn?.campaign?.errorReason;

          const isUUID = (value) => !!value.match(uuidPattern);
          if (errorMessage !== '') {
            statusMessage = errorCode + ': ' + errorMessage;
          } else if (!isUUID(plivoReturn?.ProfileUUID)) {
            statusMessage = errorCode + ': ' + plivoReturn?.ProfileUUID;
          }
        }
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              alignItems: 'center',
              verticalAlign: 'middle',
            }}
          >
            <div
              style={{
                fontWeight: 600,
                color:
                  status === 'FAILED' || status === 'EXPIRED'
                    ? '#d91e18'
                    : status === 'PROCESSING'
                    ? 'orange'
                    : '#26c281',
              }}
            >
              {status}
            </div>
            <div>
              {status === 'FAILED' && statusMessage !== '' && (
                <Tooltip title={statusMessage}>
                  <InfoIcon fontSize="small" className={classes.grayIcon} />
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      headerClassName: clsx('superStyle', dataTableScss.fullWidth),
      cellClassName: dataTableScss.fullWidth,
      enableSorting: false,
      hide: !isSuperAdmin,
      renderCell: (params) => {
        const plivoReturn = params?.row?.plivoReturn;
        let status = params?.row?.campaignStatus?.toUpperCase() ?? '';
        if (
          status === '' &&
          plivoReturn?.message?.toLowerCase()?.includes('is being processed')
        ) {
          status = 'PROCESSING';
        }
        return (
          <div>
            {(status === 'FAILED' || status === 'EXPIRED') && isSuperAdmin && (
              <Tooltip title="Re-register Campaign">
                <IconButton
                  onClick={() => {
                    setCampaignId(params?.row?.id);
                    onRegisterNewCampaign(false);
                  }}
                  color="warning"
                >
                  <AppRegistrationIcon />
                </IconButton>
              </Tooltip>
            )}

            {(status === 'FAILED' || status === 'EXPIRED') &&
            Number(successfulCampaigns?.length) > 0 &&
            isSuperAdmin ? (
              <Tooltip title="Re-submit Campaign using previous successful Campaign Data">
                <IconButton
                  onClick={() => {
                    submitSuccessfulCampaign();
                  }}
                  color="error"
                >
                  <SyncProblemIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
            {isSuperAdmin && (
              <Tooltip title="Sync Campaign Data and Number Linking">
                <IconButton
                  onClick={() => {
                    handleSyncCampaign(params?.row?.id);
                  }}
                  color="primary"
                >
                  <SyncIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ] as IColDef[];

  const onRegisterNewCampaign = (isAdd: boolean) => {
    setIsAdd(isAdd);
    const campaignsToUpdate = successfulCampaigns?.map((item) => {
      const parsed = safeJsonParse(item?.plivoReturnJson, { campaign: {} });
      return {
        ...parsed.campaign,
        subusecase: item.subuseCse,
        usecase: item.useCse,
      };
    });
    if (campaignsToUpdate?.length === 0 && campaigns?.result?.length === 1) {
      if (
        campaignTemplate &&
        (plivoBrandStatus.toLowerCase() === 'completed' ||
          plivoBrandStatus.toLowerCase() === 'auth_plus_approved')
      ) {
        setCopyCampaign(campaignTemplate);
      }
    } else {
      setParsedCampaigns(campaignsToUpdate);
      setCopyCampaign(campaignsToUpdate?.[0]);
    }
    setShowRegisterCampaignModal(true);
  };

  const onHandleTemplateUpdate = (campaignId) => {
    setCopyCampaign(
      (parsedSuccessfulCampaigns as any)?.find(
        (el) => el.campaignId === campaignId
      )
    );
  };

  const hasUnsubmittedCampaignData = useMemo(() => {
    return (
      (plivoBrandStatus.toLowerCase() === 'completed' ||
        plivoBrandStatus.toLowerCase() === 'auth_plus_approved') &&
      campaigns?.result?.length === 1 &&
      campaignTemplate?.mainTemplate === 1
    );
  }, [plivoBrandStatus, campaigns, campaignTemplate]);

  const BlinkingIcon = styled(InfoIcon)(({ theme }) => ({
    animation: 'blink 1s infinite',
    '@keyframes blink': {
      '0%': { opacity: 1 },
      '50%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
  }));

  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        numSelected={0}
        hideSearch={true}
        rightComponent={
          <div className={classes.rightComponentContainer}>
            {isSuperAdmin && (
              <div
                style={{
                  marginRight: '8px',
                }}
              >
                <Button
                  disableElevation={true}
                  onClick={async () => {
                    setIsLoadingSync(true);
                    const dataToSend = {
                      userSettings: {
                        apikey: userAtomContents?.apikey,
                        businessId: userAtomContents?.businessid,
                        userId: userAtomContents?.userid,
                        businessid: userAtomContents?.businessid,
                        userid: userAtomContents?.userid,
                      },
                    };
                    try {
                      const response = await syncProfileBrand(dataToSend);
                      if (
                        response &&
                        (response as any)?.data?.success === true
                      ) {
                        trackReSync10DLC();
                        enqueueSnackbarWithStyles({
                          message: `Profile and Brand data synced successfully`,
                          alertProps: { severity: 'success' },
                        });
                        refetchProfileBrands();
                      }
                    } catch (err) {
                      enqueueSnackbarWithStyles({
                        message: `Profile and Brand data sync failed`,
                        alertProps: { severity: 'error' },
                      });
                    }
                    setIsLoadingSync(false);
                  }}
                  variant="outlined"
                  color="info"
                  disabled={isLoadingSync}
                >
                  Re-Sync
                </Button>
              </div>
            )}
            {profileBrandsLoading ? (
              <CircularProgress size={20} style={{ margin: '8px 20px' }} />
            ) : (!plivoBrandStatus && !isProcessing) ||
              plivoBrandStatus?.toLowerCase() === 'failed' ? (
              <Button
                style={{ marginRight: '8px' }}
                onClick={() => {
                  setIsNewBrand(false);
                  setShowRegisterBrandModal(true);
                }}
                disableElevation={true}
                variant="contained"
                color="primary"
              >
                {plivoBrandStatus?.toLowerCase() === 'failed' ||
                plivoBrandRegistrationStatus?.toLowerCase() === 'failed'
                  ? 'Re-Register Brand'
                  : 'Register Brand'}
              </Button>
            ) : (
              <></>
            )}
            {(plivoBrandStatus?.toLowerCase() === 'completed' ||
              plivoBrandStatus?.toLowerCase() === 'auth_plus_approved') && (
              <>
                <Tooltip
                  title={
                    hasUnsubmittedCampaignData
                      ? 'You have an unsubmitted campaign, please review and submit'
                      : ''
                  }
                >
                  <Button
                    style={{ marginRight: '8px' }}
                    onClick={() => {
                      onRegisterNewCampaign(true);
                      trackAdd10DLCCampaign();
                    }}
                    disableElevation={true}
                    variant="outlined"
                    color="primary"
                    endIcon={hasUnsubmittedCampaignData && <BlinkingIcon />}
                  >
                    Add Campaign
                  </Button>
                </Tooltip>
                {Number(process.env.VITE_APP_ADD_MULTIPLE_BRANDS) === 1 && (
                  <Tooltip title="Register a new profile + brand to manage multiple brands">
                    <Button
                      style={{ marginRight: '8px' }}
                      onClick={() => {
                        setIsNewBrand(true);
                        setShowRegisterBrandModal(true);
                      }}
                      disableElevation={true}
                      variant="contained"
                      color="primary"
                    >
                      Add Brand
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        }
      />
    ),
    [
      plivoBrandStatus,
      profileBrandsLoading,
      isLoadingSync,
      isSuperAdmin,
      setIsLoadingSync,
      hasUnsubmittedCampaignData,
      BlinkingIcon,
      classes,
      plivoBrandRegistrationStatus,
      setShowRegisterBrandModal,
      syncProfileBrand,
      onRegisterNewCampaign,
      userAtomContents,
      enqueueSnackbarWithStyles,
      refetchProfileBrands,
      isProcessing,
    ]
  );

  const submitSuccessfulCampaign = async () => {
    try {
      enqueueSnackbarWithStyles({
        message:
          'Successful campaign indentified. Automatically re-submitting Failed campaigns with data from Succcessful campaign',
        alertProps: {
          severity: 'warning',
        },
      });
      const successfulCampaign = successfulCampaigns?.[0];
      const plivoCamp = safeJsonParse<{ campaign?: any }>(
        successfulCampaign?.plivoReturnJson,
        { campaign: {} }
      )?.campaign as any;
      const toSubmit = {
        ...plivoCamp,
        ...plivoCamp?.campaignAttributes,
        attributes: {
          affiliateMarketing: Number(
            plivoCamp?.campaignAttributes?.affiliateMarketing
          ),
          ageGated: Number(plivoCamp?.campaignAttributes?.ageGated),
          directLending: Number(plivoCamp?.campaignAttributes?.directLending),
          embeddedLink: Number(plivoCamp?.campaignAttributes?.embeddedLink),
          embeddedPhone: Number(plivoCamp?.campaignAttributes?.embeddedPhone),
          kixieCampaignId: campaignTemplate?.kixieCampaignId,
        },
      };
      delete toSubmit.campaignAttributes;
      const response = await updateCampaign({
        userSettings: {
          apikey: userAtomContents?.apikey,
          businessId: userAtomContents?.businessid,
          userid: userAtomContents?.userid,
        },
        campaign: toSubmit,
      });
      if (
        !response ||
        !('data' in response) ||
        response.data.success === false
      ) {
        enqueueSnackbarWithStyles({
          message: `Failed Campaign Registration: ${
            response && 'error' in response ? response.error : ''
          }`,
          alertProps: {
            severity: 'error',
          },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: 'Campaign Successfully Re-submitted',
          alertProps: {
            severity: 'success',
          },
        });
      }
    } catch (err) {
      enqueueSnackbarWithStyles({
        message: 'Failed Campaign Registration',
        alertProps: {
          severity: 'error',
        },
      });
    }
  };

  const brandRegistrationStatusMessage = useMemo(() => {
    if (plivoBrandRegistrationStatus.toLowerCase() === 'failed') {
      const isUUID = (value) => !!value.match(uuidPattern);
      if (
        brandData?.plivoBrandJson?.ErrorCode ||
        brandData?.plivoBrandJson?.ErrorMessage ||
        brandData?.plivoBrandJson?.ProfileUUID
      ) {
        if (brandData?.plivoBrandJson?.ErrorMessage !== '') {
          return (
            brandData?.plivoBrandJson?.ErrorCode +
            ': ' +
            brandData?.plivoBrandJson?.ErrorMessage
          );
        } else if (!isUUID(brandData?.plivoBrandJson?.ProfileUUID)) {
          return (
            brandData?.plivoBrandJson?.ErrorCode +
            ': ' +
            brandData?.plivoBrandJson?.ProfileUUID
          );
        }
      } else if (
        brandData?.plivoBrandJson?.brand?.declinedReasons?.length > 0
      ) {
        let combinedMessage = '';
        brandData?.plivoBrandJson?.brand?.declinedReasons?.forEach((reason) => {
          combinedMessage += reason?.code + ': ' + reason?.message + '\n';
        });
        return combinedMessage;
      }
    } else {
      return '';
    }
  }, [brandData, plivoBrandRegistrationStatus]);

  const formattedCreatedDate = useMemo(() => {
    if (brandData?.createddate) {
      return DateTime.fromISO(brandData?.createddate, {
        zone: businessDataCached.current?.timezone_php,
      }).toLocaleString(DateTime.DATETIME_SHORT);
    }
    return '';
  }, [brandData, brandRegistrationStatusMessage]);

  return (
    <Paper className={classes.brandContainer}>
      <div className={classes.brandTitleRow}>
        <div style={{ marginBottom: '16px' }}>
          <div className={classes.titleText}>
            {brandData?.plivoProfileJson?.profile?.profileAlias ?? ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
            <div>{brandData?.brandId ?? ''}</div>
            <div>-</div>
            <div
              style={{
                fontWeight: 700,
                color: isProcessing
                  ? 'orange'
                  : plivoBrandRegistrationStatus.toLowerCase() ===
                      'completed' ||
                    plivoBrandRegistrationStatus.toLowerCase() === 'created' ||
                    plivoBrandRegistrationStatus.toLowerCase() ===
                      'auth_plus_approved'
                  ? '#26c281'
                  : '#d91e18',
              }}
            >
              {isProcessing ? 'PROCESSING' : plivoBrandRegistrationStatus}
            </div>
            <div>
              {plivoBrandRegistrationStatus.toLowerCase() === 'failed' &&
                brandRegistrationStatusMessage !== '' && (
                  <Tooltip title={brandRegistrationStatusMessage}>
                    <InfoIcon fontSize="small" className={classes.grayIcon} />
                  </Tooltip>
                )}
            </div>
          </div>
          <div>
            {brandData?.brandStatus?.toLowerCase() !== 'failed'
              ? brandData?.brandStatus
              : ''}
          </div>
          <div>
            {' '}
            <Tooltip title="Created Date">
              <>{formattedCreatedDate}</>
            </Tooltip>
          </div>
        </div>

        <div className={classes.titleText} style={{ textAlign: 'right' }}>
          {brandData?.plivoBrandJson?.brand?.companyName ?? ''}
        </div>
      </div>
      <DataTable
        loading={
          campaignsLoading ||
          campaignSyncLoading ||
          isLoadingSync ||
          profileBrandsLoading
        }
        rows={rowsCached?.current ?? []}
        columns={columns}
        ToolbarComponent={ToolbarComponent}
        sortModel={sortModel}
        setGridSortModel={setGridSortModel}
      />
      <RegisterCampaignModal
        show={showRegisterCampaignModal}
        handleClose={() => {
          setShowRegisterCampaignModal(false);
          setIsAdd(false);
        }}
        campaignTemplate={selectedCampaignToCopy}
        brandId={brandData?.brandId ?? ''}
        userAtomContents={userAtomContents}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        updateCampaign={updateCampaign}
        successfulCampaigns={parsedSuccessfulCampaigns}
        handleTemplateUpdate={onHandleTemplateUpdate}
        isAdd={isAdd}
        campaignId={campaignId}
        setRefetchToggleForCampaigns={refetchCampaigns}
      />
      <RegisterBrand
        show={showRegisterBrandModal}
        setRefetchToggleForProfileBrands={refetchProfileBrands}
        campaignTemplate={campaignTemplate ?? {}}
        handleClose={() => {
          setShowRegisterBrandModal(false);
        }}
        setRefetchToggleForCampaigns={refetchCampaigns}
        brandData={brandData}
        isNewBrand={isNewBrand}
        profileBrands={profileBrands}
        profileBrandsSuccess={profileBrandsSuccess}
        profileBrandsLoading={profileBrandsLoading}
        businessProfileBrands={businessProfileBrands}
      />
    </Paper>
  );
}
